/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/no-unescaped-entities */
import {
  CrossInCircleIcon,
  SearchIcon,
  useDebounce,
  IconButton,
  BareButton,
} from "@setel/web-ui";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArticleList } from "./article-list";
import {
  HelpCentrePath,
  HELP_CENTRE_SEARCH_DEBOUNCE_DELAY,
} from "~/lib/types/searchHistory";
import ContainerFluid from "~/lib/components/layout/ContainerFluid";
import { Input } from "~/lib/components/layout/Form/Input/Input";
import { useSearchZendeskArticles } from "~/services/queries/zendesk.queries";
import { useHelpCentreHistory } from "../../utils/hooks/useHistory";
import { SearchHistoryList } from "./search-history-list";
import { useLocation } from "@remix-run/react";
import { LocaleLink, useLocaleNavigate } from "~/lib/components/link";
import { useTranslation } from "~/lib/utils/store/useTranslation";
import { WebSearchBar } from "./search-bar";
import { useGetMostArticles } from "~/services/queries/api.zendesk.queries";
import { MOST_ARTICLES_LIMIT } from "~/lib/utils/const";
import { MostSearchArticleList } from "./most-search-article-list";
import { isMiniApp, isEnterAsMiniApp } from "~/lib/utils/helper/isMiniApp";
import { HamburgerMenu } from "../navigation/HamburgerMenu";
import { useHelpCentreConfig } from "~/services/queries/variable.queries";
import csImgUrl from "./customer-support.svg";

export const HelpCentreSearchBar = () => {
  return (
    <>
      <HelpCenterMobileSearchBar />
      <HelpCenterWebSearchBar />
    </>
  );
};

const HelpCenterWebSearchBar = () => {
  const { t } = useTranslation();

  return (
    <>
      {/* to allow width match with the content */}
      <ContainerFluid className="top-0 w-full md:pt-[72px] md:pb-[76px] hidden xl:block bg-carbon-700 relative">
        <div
          data-testid="web-search"
          className="mx-auto w-full 2xl:px-[128px] px-[20px] md:px-[50px] xl:px-[100px] max-w-screen-md md:max-w-8xl"
        >
          <div className={"w-full relative"}></div>
          <h2 className="text-white mb-8 md:leading-[60px] text-5xl font-bold relative z-10 md:w-[420px] lg+:w-full xl:w-full">
            {t("searchTitle")}
          </h2>

          <WebSearchBar />
        </div>
      </ContainerFluid>
    </>
  );
};

const HelpCenterMobileSearchBar = () => {
  const { t } = useTranslation();
  return (
    <>
      <div
        className="bg-carbon-700 relative w-full px-5 py-6 overflow-hidden xl:hidden z-[10]"
        data-testid="mobile-search"
      >
        <img
          className="-z-1 absolute bottom-0 right-0"
          src={csImgUrl}
          width={176}
          height={137}
          alt="setel"
        />
        <div className="pt-5">
          <h2 className="text-white mb-4 text-2xl font-semibold z-1 w-[200px]">
            {t("searchTitle")}
          </h2>
        </div>
      </div>
    </>
  );
};

export interface HelpCenterSearchBarProps {
  onSearchTopic?: () => void;
}

export const SearchForm = () => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [showNoCloseButton, setShowNoCloseButton] = useState(false);
  const [isMiniAppUser, setIsMiniAppUser] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const location = useLocation();
  const navigate = useLocaleNavigate();
  const { t, i18n } = useTranslation();
  const [searchHistories, addSearchHistory, removeSearchHistories] =
    useHelpCentreHistory();
  const { data: mostSearchHistories, isLoading: isLoadingArticles } =
    useGetMostArticles({ limit: MOST_ARTICLES_LIMIT, locale: i18n.language });
  const debouncedSearchInput = useDebounce(
    searchInput,
    HELP_CENTRE_SEARCH_DEBOUNCE_DELAY
  );
  const { data: helpCentreConfig, isLoading } = useHelpCentreConfig();
  const isSurfacingPage = location.pathname.includes("/article/surfacing");

  const navigateToSearchPage = useCallback(() => {
    addSearchHistory({ searchedInput: searchInput });
    navigate(`${HelpCentrePath.SEARCH_ARTICLE}?query=${searchInput || ""}`);
  }, [searchInput]);
  const { data: searchData } = useSearchZendeskArticles(
    {
      query: debouncedSearchInput,
      locale: i18n.language,
      per_page: 5,
    },
    {
      onSuccess: () => {
        if (!isDropdownVisible) {
          setDropdownVisible(true);
        }
      },
    }
  );

  useEffect(() => {
    setSearchInput("");
    setDropdownVisible(false);
  }, [location.pathname]);

  useEffect(() => {
    setIsMiniAppUser(isMiniApp() || isEnterAsMiniApp());
  }, []);

  const renderSearchBar = () => {
    if (isMiniAppUser) {
      // render nothing for surfacing page
      if (isSurfacingPage) {
        return null;
      }

      return (
        <div
          className={`border-b w-screen sticky flex py-2 px-3 space-x-3 items-center h-16 justify-around top-0 z-49 bg-white shadow-header`}
        >
          <button
            className="text-lightgrey h-12 w-full flex items-center z-1 bg-[#F5F5F5] rounded-md pr-0 text-base placeholder-[#788494] input-search-bar"
            onClick={() => {
              navigate("/mobile-search");
            }}
          >
            <SearchIcon className="mx-2.5 z-1 text-[#000000]" />
            {t("searchQuestions")}
          </button>
          <div className="pt-1">
            <img
              src="/images/categories.svg"
              alt="Menu"
              onClick={() => setShowNoCloseButton(true)}
              width={24}
              height={24}
            />
            <HamburgerMenu
              helpCentreConfig={helpCentreConfig}
              showNoCloseButton={showNoCloseButton}
              setShowNoCloseButton={setShowNoCloseButton}
            />
          </div>
        </div>
      );
    }

    return (
      <Input
        className="flex items-center z-[60]"
        inputClass={`bg-[#F5F5F5] rounded-md h-[48px] pr-0 text-base placeholder-[#788494] input-search-bar`}
        placeholder={t("searchQuestions")}
        onFocus={() => {
          if (
            !searchInput &&
            searchHistories &&
            searchHistories?.length !== 0 &&
            mostSearchHistories
          ) {
            setDropdownVisible(true);
            return;
          }

          if (
            searchData &&
            searchData?.results.length !== 0 &&
            !isDropdownVisible
          ) {
            setDropdownVisible(true);
          }
        }}
        onClick={() => setDropdownVisible(true)}
        onBlur={() => {
          setDropdownVisible(false);
        }}
        value={searchInput}
        onChange={(e) => {
          setSearchInput(e.target.value);
        }}
        ref={inputRef}
        before={
          <IconButton
            data-testid="web-focus-input-button"
            onClick={() => {
              inputRef.current?.focus();
            }}
          >
            <SearchIcon />
          </IconButton>
        }
        onKeyDown={() => {
          /** TODO: navigate thru dropdown using arrow key */
        }}
        after={
          <>
            {searchInput && (
              <IconButton
                className="pl-4"
                type="reset"
                data-testid="web-remove-input-button"
              >
                <CrossInCircleIcon className="text-lightgrey" />{" "}
              </IconButton>
            )}
          </>
        }
      />
    );
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!searchInput) {
          return;
        }

        navigateToSearchPage();
      }}
      onReset={() => {
        setSearchInput("");
      }}
      className={"relative search-form"}
    >
      {renderSearchBar()}
      <div
        className={`bg-white w-full absolute drop-shadow-lg rounded-[8px] mt-[8px] z-[75] ${
          isDropdownVisible ? "block" : "hidden"
        }`}
        onMouseDown={(e) => {
          e.preventDefault();
        }}
      >
        {!debouncedSearchInput &&
          searchHistories &&
          searchHistories?.length !== 0 && (
            <>
              <div className="flex mx-5 mt-4">
                <p className="font-semibold text-xl">{t("searchHistory")}</p>
                <BareButton
                  className="text-[15px] leading-[21px] font-medium text-brand-500 pl-6"
                  onClick={() => {
                    removeSearchHistories();
                    /** TODO: show related articles, and remove this */
                    setDropdownVisible(false);
                  }}
                >
                  {t("clear")}
                </BareButton>
              </div>
              <SearchHistoryList
                variant="web"
                onClick={addSearchHistory}
                items={searchHistories}
              />
            </>
          )}
        {!debouncedSearchInput && mostSearchHistories && (
          <div className="pb-6">
            <div className="flex mx-5 mt-4">
              <p className="font-semibold text-xl">{t("mostSearch")}</p>
            </div>
            <MostSearchArticleList variant="web" items={mostSearchHistories} />
          </div>
        )}
        {searchData && (
          <>
            <ArticleList
              articles={searchData.results}
              searchedInput={debouncedSearchInput}
              addSearchHistory={addSearchHistory}
              locale={i18n.language.toLowerCase()}
            />

            <LocaleLink
              to={`${HelpCentrePath.SEARCH_ARTICLE}${
                searchInput && `?query=${searchInput}`
              }`}
              className="text-brand-500 flex px-8 py-6 hover:bg-brand-50 rounded-b-xl"
              onClick={() => addSearchHistory({ searchedInput: searchInput })}
            >
              <SearchIcon className="w-6 h-6 mr-2 my-auto" />
              <span>
                {t("viewMoreResult", {
                  searchInput: debouncedSearchInput,
                })}
              </span>
            </LocaleLink>
          </>
        )}
      </div>
      {isDropdownVisible && (
        <div className="w-[100vw] h-[100vh] fixed bg-darkgrey opacity-80 top-0 left-0 z-50"></div>
      )}
    </form>
  );
};
